import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ChevronRight } from 'lucide-react';
import { LANDING } from '@/constants/testIds';

const LOGO_TRANSPARENT = 'https://customer-assets-39nsmqrw.emergentagent.net/job_dining-portal-119/artifacts/5i4j17yj_ChatGPT_Image_Aug_31__2026__07_35_46_PM-removebg-preview.png';
const HERO_IMG = 'https://images.unsplash.com/photo-1636405189493-181ecf851006?crop=entropy&cs=srgb&fm=jpg&q=85&w=800';

export default function Landing() {
  const { user, enterGuest } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      const path = user.role === 'admin' ? '/admin' : user.role === 'vendor' ? '/vendor/dashboard' : '/diner/discover';
      navigate(path, { replace: true });
    }
  }, [user, navigate]);

  const googleAuthUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f8f7fb' }}>

      {/* Hero */}
      <div className="relative overflow-hidden text-white px-6 pt-14 pb-16 flex flex-col items-center text-center"
        style={{ background: 'linear-gradient(135deg, #3D0070 0%, #5B21B6 60%, #7C3AED 100%)' }}>
        <img src={LOGO_TRANSPARENT} alt="Dine Around"
          className="h-16 w-auto object-contain mb-3"
          style={{ filter: 'brightness(0) invert(1)' }} />
        <p className="text-purple-200 text-[11px] font-bold tracking-[0.3em] uppercase mb-4">
          Discover. Dine. Delight.
        </p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
          Exclusive dining offers,<br />right near you.
        </h1>
        <p className="text-purple-200 text-sm mt-3 max-w-xs">
          Scan QR codes, explore menus, and discover great restaurants around you.
        </p>

        {/* Hero image strip */}
        <div className="w-full max-w-xs mt-8 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <img src={HERO_IMG} alt="Dining" className="w-full h-36 object-cover opacity-80" />
        </div>
      </div>

      {/* Auth CTA */}
      <div className="flex-1 px-5 py-8 max-w-sm mx-auto w-full flex flex-col gap-3">

        {/* Continue with Google */}
        <a
          data-testid={LANDING.dinerBtn}
          href={googleAuthUrl}
          className="w-full flex items-center justify-center gap-3 py-4 bg-white rounded-2xl shadow-md border border-gray-200 text-gray-700 font-bold text-sm hover:shadow-lg hover:border-gray-300 transition-all active:scale-[0.99]"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </a>

        {/* Browse as Guest */}
        <button
          data-testid="browse-as-guest-landing-btn"
          onClick={() => { enterGuest(); navigate('/diner/discover'); }}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-purple-200 text-[#5B21B6] font-semibold text-sm hover:bg-purple-50 transition-colors"
        >
          Browse as Guest
          <ChevronRight className="w-4 h-4" />
        </button>

        <p className="text-center text-[11px] text-gray-400 mt-1">
          Are you a restaurant owner?{' '}
          <a href="/vendor/login" className="text-[#5B21B6] font-semibold hover:underline">Vendor Login</a>
        </p>

      </div>
    </div>
  );
}
